import { useParams } from "react-router-dom";

import type { AcuityHistoryEntry, UUID } from "@/core/types";
import { AcuityScoreBadge } from "@/features/acuity/components/AcuityScoreBadge";
import { AcuityStatusBadge } from "@/features/acuity/components/AcuityStatusBadge";
import { cn } from "@/lib/helpers/cn";
import dayjs from "@/lib/helpers/dayjs";
import {
  Timeline,
  TimelineDetail,
  TimelineDetails,
  TimelineItem,
  TimelineTimestamp,
} from "@/ui2/molecules/Timeline";

import { useAcuityHistory } from "../api/queries/useAcuityHistory";

import { AcuityHistoryTimelineSkeleton } from "./AcuityHistoryTimelineSkeleton";

type Params = {
  patientId: UUID;
};

export function AcuityHistoryTimeline() {
  const { patientId } = useParams<Params>();
  const { data: acuityHistory, isLoading } = useAcuityHistory({ patientId });

  if (isLoading) return <AcuityHistoryTimelineSkeleton />;

  const latestRecord = acuityHistory.reduce<AcuityHistoryEntry | undefined>((latest, record) => {
    if (!latest) return record;

    return dayjs(record.resolved_at || record.requested_at).valueOf() >
      dayjs(latest.resolved_at || latest.requested_at).valueOf()
      ? record
      : latest;
  }, undefined);

  return (
    <Timeline>
      {acuityHistory.map((record) => (
        <TimelineRecord
          isLatest={record.entity_id === latestRecord?.entity_id}
          key={record.entity_id}
          {...record}
        />
      ))}
    </Timeline>
  );
}

function TimelineRecord(props: AcuityHistoryEntry & { isLatest: boolean }) {
  const {
    additional_details,
    isLatest,
    reasoning,
    rejection_reason,
    requestor_name,
    resolved_at,
    score,
    status,
  } = props || {};

  const DETAIL_LIST: Record<string, string>[] = [
    {
      label: "Requested by",
      value: requestor_name,
    },
    {
      label: "Reasoning",
      value: reasoning,
    },
    {
      label: "Additional details",
      value: additional_details,
    },
    {
      label: "Rejection reason",
      value: rejection_reason,
    },
  ];

  return (
    <TimelineItem isLatest={isLatest}>
      <div className="tw:flex tw:min-w-40 tw:flex-col tw:gap-y-1 tw:border-r tw:border-fill-quaternary tw:pr-4">
        {resolved_at && <TimelineTimestamp dateTime={resolved_at} />}
        <AcuityScoreBadge
          acuityScore={score}
          className={cn("tw:m-0 tw:self-baseline", isLatest ? "tw:opacity-100" : "tw:opacity-50")}
        />
      </div>
      <TimelineDetails>
        <TimelineDetail label="Status" value={<AcuityStatusBadge status={status} />} />

        {DETAIL_LIST.filter((item) => Boolean(item.value)).map((item) => (
          <TimelineDetail key={item.label} label={item.label} value={item.value} />
        ))}
      </TimelineDetails>
    </TimelineItem>
  );
}
